import type { Menu } from "@/contexts/SidebarContext";
import { EQUIPMENT_PERMISSIONS, Permission, PermissionGroups } from "@/types/permissions";
import { getAgentDisplayName } from "@/utils/agentCache";
import { featureFlags } from "@/utils/featureFlags";

import { AGENT_RUNTIME_ADMIN_ROLES, RAG_REVIEWER_PERMISSIONS, RAG_REVIEWER_ROLES } from "./protectedRoutes";
import { PATH } from "./router/routerConfig";

const createHelpdeskMenu = (t: any): Menu => ({
  path: PATH.EDGE_HELPDESK,
  title: t("Helpdesk"),
  icon: "SupportAgent",
  productRouteKey: "edge_helpdesk/landing",
  applicationCapabilities: ["helpdesk.use"],
  breadcrumb: { title: t("Help Center") }
});

const createHelpdeskAdminMenu = (t: any): Menu => ({
  path: PATH.EDGE_HELPDESK_ADMIN,
  title: t("Helpdesk Admin"),
  icon: "Admin",
  productRouteKey: "edge_helpdesk/admin",
  applicationCapabilities: ["helpdesk.admin", "helpdesk.manage"]
});

const createLlmGatewayMenu = (t: any): Menu => ({
  path: PATH.LLMGW,
  title: t("Models & Routing"),
  icon: "Tune",
  productRouteKey: "llmgw/landing",
  applicationCapabilities: ["llmgw.read"]
});

const createLlmGatewayAdminMenu = (t: any): Menu => ({
  path: PATH.LLMGW_ADMIN,
  title: t("Gateway Administration"),
  icon: "Admin",
  productRouteKey: "llmgw_admin/admin",
  applicationCapabilities: ["llmgw.admin", "llmgw.manage_models", "llmgw.manage_credentials"]
});

const createEngineeringAppMenu = (t: any): Menu => ({
  path: PATH.ENGINEERING_APP,
  title: t("Engineering"),
  icon: "Architecture",
  breadcrumb: { title: t("Engineering") },
  subMenu: [
    {
      path: PATH.ENGINEERING_APP,
      title: t("Workspaces"),
      icon: "AccountTree",
      breadcrumb: { title: t("Engineering workspaces") }
    },
    {
      title: t("Common resources"),
      icon: "FolderShared",
      subMenu: [
        {
          path: PATH.ENGINEERING_KNOWLEDGE,
          title: t("Knowledge"),
          icon: "FolderShared",
          breadcrumb: { title: t("Common Engineering knowledge") }
        },
        {
          path: PATH.ENGINEERING_TEMPLATES,
          title: t("Templates and samples"),
          icon: "Description",
          breadcrumb: { title: t("Common Engineering templates and samples") }
        }
      ]
    }
  ]
});

const createSettingsWorkMenu = (t: any): Menu => ({
  title: t("Work"),
  description: t("Configure the business processes used by Work."),
  icon: "EmojiEvents",
  subMenu: [
    {
      path: PATH.SETTINGS_WORK_SALES_PIPELINE,
      title: t("Sales Pipeline"),
      icon: "Tune",
      permissions: [Permission.TENANT],
      breadcrumb: { title: t("Sales Pipeline") }
    },
    {
      path: PATH.SETTINGS_WORK_SUPPORT_PIPELINE,
      title: t("Support Pipeline"),
      icon: "SupportAgent",
      permissions: [Permission.TENANT],
      breadcrumb: { title: t("Support Pipeline") }
    },
    {
      path: PATH.SETTINGS_WORK_LEAD_PIPELINE,
      title: t("Lead Pipeline"),
      icon: "TrendingUp",
      permissions: [Permission.TENANT],
      breadcrumb: { title: t("Lead Pipeline") }
    },
    {
      path: PATH.SETTINGS_WORK_OUTCOME_PACKS,
      title: t("Outcome Packs"),
      icon: "Assignment",
      permissions: [Permission.TENANT],
      breadcrumb: { title: t("Outcome Packs") }
    }
  ]
});

const createSettingsWorkspaceMenu = (t: any, isSandboxTenant: boolean): Menu => ({
  title: t("Workspace"),
  description: t("Manage workspace identity, lifecycle, and billing."),
  icon: "Apartment",
  subMenu: [
    {
      path: PATH.SETTINGS_WORKSPACE_OVERVIEW,
      title: t("Overview"),
      icon: "Settings",
      permissions: [Permission.TENANT],
      breadcrumb: { title: t("Workspace Overview") }
    },
    {
      path: PATH.SETTINGS_WORKSPACE_SERVICE_ACCOUNTS,
      title: t("Service accounts"),
      icon: "Email",
      permissions: [Permission.TENANT],
      breadcrumb: { title: t("Workspace Service Accounts") }
    },
    {
      path: "/setting/tenants",
      title: t("Workspaces"),
      icon: "Apartment",
      permissions: [Permission.TENANT],
      breadcrumb: { title: t("Workspaces") }
    },
    {
      path: "/setting/tenants/sandbox",
      title: t("Sandbox Workspaces"),
      icon: "Apartment",
      permissions: [Permission.TENANT],
      hidden: isSandboxTenant,
      breadcrumb: { title: t("Sandbox Workspaces") }
    },
    ...(isSandboxTenant
      ? []
      : [
          {
            path: PATH.BILLING,
            title: t("Billing"),
            icon: "CreditCard",
            permissions: [...PermissionGroups.ADMIN],
            breadcrumb: { title: t("Billing") }
          }
        ])
  ]
});

const createSettingsAgentsMenu = (t: any): Menu => ({
  title: t("Agents"),
  description: t("Manage agent capabilities and connected tools."),
  icon: "SmartToy",
  subMenu: [
    {
      path: PATH.SETTINGS_AGENTS_SKILLS_STORE,
      title: t("Skills Store"),
      icon: "AutoAwesome",
      permissions: [Permission.TENANT],
      breadcrumb: { title: t("Skills Store") }
    },
    {
      path: PATH.SYSTEM_INTEGRATIONS_MCP,
      title: t("MCP Servers"),
      icon: "IntegrationInstructions",
      permissions: [Permission.APP_INTEGRATIONS],
      breadcrumb: { title: t("MCP Servers") }
    },
    {
      path: PATH.AGENTS_TOOL,
      title: t("Tools"),
      icon: "Build",
      hidden: true,
      permissions: [Permission.AGENT_TOOLS],
      breadcrumb: { title: t("Tools") }
    }
  ]
});

const createSettingsKnowledgeMenu = (t: any): Menu => ({
  title: t("Knowledge"),
  icon: "Folder",
  subMenu: [
    {
      path: PATH.SETTING_KNOWLEDGE_MANAGER,
      title: t("Knowledge Manager"),
      icon: "AutoAwesome",
      breadcrumb: { title: t("Knowledge Manager") }
    },
    {
      path: PATH.SETTING_KNOWLEDGE_PACKS,
      title: t("Knowledge Packs"),
      icon: "Folder",
      permissions: [...PermissionGroups.KNOWLEDGE_PACKS],
      breadcrumb: { title: t("Knowledge Packs") }
    },
    {
      path: "/setting/rag",
      title: t("RAG Runtime"),
      icon: "FactCheck",
      permissions: [...RAG_REVIEWER_PERMISSIONS],
      roles: [...RAG_REVIEWER_ROLES],
      breadcrumb: { title: t("RAG Runtime") }
    }
  ]
});

const createSettingsCrmMenu = (t: any): Menu => ({
  path: PATH.SETTING_CRM,
  title: t("CRM"),
  icon: "BusinessCenter",
  permissions: [Permission.APP_INTEGRATIONS],
  breadcrumb: { title: t("CRM") },
  subMenu: [
    {
      path: PATH.SETTING_CRM_SYNC,
      title: t("Connections"),
      icon: "ListAlt",
      permissions: [Permission.APP_INTEGRATIONS]
    },
    {
      path: PATH.SETTING_CRM_USER_MAPPING,
      title: t("User Mapping"),
      icon: "Groups",
      permissions: [Permission.APP_INTEGRATIONS]
    },
    {
      path: PATH.SETTING_CRM_SYNC_LOGS,
      title: t("Sync Logs"),
      icon: "ListAlt",
      permissions: [Permission.APP_INTEGRATIONS]
    },
    ...(featureFlags.showCrmSignals
      ? [
          {
            path: PATH.SETTING_CRM_SIGNALS,
            title: t("Signal Settings"),
            icon: "Tune",
            permissions: [Permission.APP_INTEGRATIONS]
          }
        ]
      : [])
  ]
});

const createSettingsDataMenu = (t: any): Menu => ({
  title: t("Data"),
  description: t("Manage business data, knowledge, and data operations."),
  icon: "BusinessCenter",
  subMenu: [
    createSettingsKnowledgeMenu(t),
    {
      path: "/setting/tenants/industry-forms",
      title: t("Industry Templates"),
      icon: "ListAlt",
      permissions: [Permission.TENANT],
      breadcrumb: { title: t("Industry Templates") }
    },
    {
      path: PATH.TENANT_SAMPLE_DATA,
      title: t("Sample Data"),
      icon: "AutoFixHigh",
      permissions: [Permission.TENANT],
      breadcrumb: { title: t("Sample Data") }
    },
    {
      path: "/setting/tenants/files",
      title: t("Files"),
      icon: "Folder",
      permissions: [Permission.TENANT],
      breadcrumb: { title: t("Files") }
    }
  ]
});

const createSettingsAppsMenu = (t: any): Menu => ({
  title: t("Apps"),
  description: t("Configure connected products and application behavior."),
  icon: "Apps",
  subMenu: [
    {
      path: PATH.SETTINGS_APPS,
      title: t("Installed Apps"),
      icon: "Apps",
      permissions: [Permission.TENANT],
      breadcrumb: { title: t("Installed Apps") }
    },
    {
      title: t("Helpdesk"),
      icon: "SupportAgent",
      subMenu: [
        createHelpdeskAdminMenu(t),
        {
          path: PATH.SETTINGS_APPS_HELPDESK_POLICY,
          title: t("Helpdesk Policy"),
          icon: "Tune",
          permissions: [Permission.TENANT],
          breadcrumb: { title: t("Helpdesk Policy") }
        },
        {
          path: PATH.SETTINGS_APPS_HELPDESK_OPERATIONS,
          title: t("Helpdesk Operations"),
          icon: "SupportAgent",
          permissions: [Permission.TENANT],
          breadcrumb: { title: t("Helpdesk Operations") }
        }
      ]
    }
  ]
});

const createSettingsIntegrationsMenu = (t: any): Menu => ({
  path: PATH.SYSTEM_INTEGRATIONS,
  title: t("Integrations"),
  icon: "IntegrationInstructions",
  permissions: [Permission.APP_INTEGRATIONS],
  breadcrumb: { title: t("Integrations") },
  subMenu: [
    {
      path: PATH.SYSTEM_INTEGRATIONS_INBOUND,
      title: t("Inbound Integrations"),
      icon: "PublishedWithChanges",
      permissions: [Permission.APP_INTEGRATIONS],
      subMenu: [
        {
          path: PATH.SETTINGS_APPS_EMAIL_VENDOR_PROFILES,
          title: t("Vendor email profiles"),
          icon: "Language",
          permissions: [Permission.TENANT],
          breadcrumb: { title: t("Vendor email profiles") }
        },
        {
          path: PATH.SETTINGS_APPS_EMAIL_AUTOMATION_POLICY,
          title: t("Automation candidate policy"),
          icon: "Tune",
          permissions: [Permission.TENANT],
          breadcrumb: { title: t("Automation candidate policy") }
        }
      ]
    },
    {
      path: PATH.SYSTEM_INTEGRATIONS_MESSAGING,
      title: t("Messaging"),
      icon: "Forum",
      permissions: [Permission.APP_INTEGRATIONS]
    },
    createSettingsCrmMenu(t)
  ]
});

const createSettingsAiModelsMenu = (t: any): Menu => ({
  path: PATH.SETTINGS_AI_MODELS,
  title: t("AI & Models"),
  description: t("Configure AI providers, models, routing, and runtime controls."),
  icon: "SmartToy",
  subMenu: [
    {
      title: t("My AI Usage"),
      icon: "TrendingUp",
      applicationCapabilities: ["llmgw.read"],
      underDevelopment: true
    },
    {
      path: PATH.SETTINGS_AI_MODELS_PROVIDERS,
      title: t("Providers & API Keys"),
      icon: "Settings",
      permissions: [Permission.TENANT],
      breadcrumb: { title: t("Providers & API Keys") }
    },
    createLlmGatewayMenu(t),
    createLlmGatewayAdminMenu(t),
    {
      path: PATH.SETTINGS_AI_MODELS_RUNTIME,
      title: t("Runtime"),
      icon: "Tune",
      permissions: [Permission.TENANT],
      breadcrumb: { title: t("AI Runtime") }
    },
    {
      path: PATH.SETTINGS_AI_MODELS_MODEL_OVERRIDES,
      title: t("Model Overrides"),
      icon: "Settings",
      permissions: [Permission.TENANT],
      breadcrumb: { title: t("Model Overrides") }
    },
    {
      path: PATH.SETTINGS_AI_MODELS_MODEL_MAPPING,
      title: t("Model Mapping"),
      icon: "Tune",
      permissions: [Permission.TENANT],
      breadcrumb: { title: t("Model Mapping") }
    },
    {
      path: PATH.SETTINGS_AI_MODELS_BUDGET_ALERTS,
      title: t("Budget Alerts"),
      icon: "FactCheck",
      permissions: [Permission.TENANT],
      breadcrumb: { title: t("Budget Alerts") }
    },
    {
      title: t("Developer Guides"),
      icon: "AutoFixHigh",
      applicationCapabilities: ["llmgw.read"],
      underDevelopment: true
    }
  ]
});

const createSettingsAccessControlMenu = (t: any): Menu => ({
  path: PATH.SYSTEM,
  title: t("Access control"),
  icon: "Admin",
  breadcrumb: { title: t("Access control") },
  subMenu: [
    {
      path: PATH.SYSTEM_USERS,
      title: t("Users"),
      icon: "Person",
      permissions: [Permission.GROUP]
    },
    {
      path: PATH.SYSTEM_GROUPS,
      title: t("Groups"),
      icon: "Groups",
      permissions: [Permission.GROUP]
    },
    {
      path: PATH.SYSTEM_PERMISSIONS,
      title: t("Permissions"),
      icon: "Assignment",
      permissions: [Permission.PERMISSION]
    },
    {
      path: PATH.SYSTEM_POLICIES,
      title: t("Policies"),
      icon: "Tune",
      permissions: [Permission.GROUP]
    },
    {
      path: PATH.SETTINGS_ACCESS_CONTROL_DASHBOARD,
      title: t("Dashboard Access"),
      icon: "Admin",
      permissions: [Permission.TENANT],
      breadcrumb: { title: t("Dashboard Access") }
    }
  ]
});

const createSettingsLegacyToolsMenu = (t: any): Menu => ({
  path: PATH.TOOLS,
  title: t("Tools"),
  icon: "Build",
  hidden: true,
  permissions: [Permission.AGENT_TOOLS],
  subMenu: [
    {
      path: PATH.TOOLS_APP,
      title: t("Tools"),
      icon: "Build",
      permissions: [Permission.AGENT_TOOLS]
    },
    {
      path: PATH.TOOLS_TEMPLATE,
      title: t("Available Tools"),
      icon: "Work",
      permissions: [Permission.AGENT_TOOLS]
    },
    ...(featureFlags.showOldToolPrompts
      ? [
          {
            path: PATH.TOOLS_PROMPTS,
            title: t("Prompts"),
            icon: "ListAlt",
            permissions: [Permission.AGENT_TOOLS]
          },
          {
            path: PATH.TOOLS_CHAINS,
            title: t("Chains"),
            icon: "ListAlt",
            permissions: [Permission.AGENT_TOOLS]
          },
          {
            path: PATH.TOOLS_TOPICS,
            title: t("Topics"),
            icon: "ListAlt",
            permissions: [Permission.AGENT_TOOLS]
          }
        ]
      : [])
  ]
});

export function getSettingsMenuItems(t: any, isSandboxTenant: boolean = false): Menu[] {
  return [
    createSettingsWorkspaceMenu(t, isSandboxTenant),
    createSettingsWorkMenu(t),
    createSettingsAgentsMenu(t),
    createSettingsDataMenu(t),
    createSettingsAppsMenu(t),
    createSettingsIntegrationsMenu(t),
    createSettingsAiModelsMenu(t),
    createSettingsAccessControlMenu(t),
    {
      path: PATH.AUDIT,
      title: t("Audit"),
      icon: "RocketLaunch",
      permissions: [Permission.AUDIT],
      breadcrumb: { title: t("Audit") }
    },
    createSettingsLegacyToolsMenu(t)
  ];
}

/**
 * Returns the sidebar menu array, filtered by current feature flags.
 */
export function getSidebarMenus(t: any, isSandboxTenant: boolean = false): Menu[] {
  const itHelpdeskMenu = createHelpdeskMenu(t);

  return [
    {
      path: PATH.HOME_PAGE,
      title: t("Home"),
      icon: "Home",
      breadcrumb: {
        title: t("Home")
      }
    },
    {
      path: PATH.SALES,
      title: t("Work"),
      icon: "EmojiEvents",
      permissions: [
        Permission.SALES_ACTIVITY,
        Permission.SALES_LEAD,
        Permission.SUPPORT_CASE_VIEW,
        Permission.SUPPORT_CASE_MANAGE
      ],
      breadcrumb: {
        title: t("Work")
      },
      subMenu: [
        {
          path: PATH.AGENTS_MY,
          title: t("My Agents"),
          icon: "Forum",
          permissions: [Permission.TRIAGE_SUB_AGENT],
          breadcrumb: {
            title: t("My Agents")
          }
        },
        {
          path: PATH.SALES_OPEN_ACTIVITIES,
          title: t("Open Activities"),
          icon: "Assignment",
          permissions: [Permission.SALES_ACTIVITY],
          breadcrumb: {
            title: t("Open Activities")
          }
        },
        ...(featureFlags.showSalesEscalation
          ? [
              {
                path: PATH.SALES_ESCALATION,
                title: t("Escalations"),
                icon: "Campaign",
                permissions: [Permission.SALES_ACTIVITY]
              }
            ]
          : []),
        ...(featureFlags.showSalesNews
          ? [
              {
                path: PATH.SALES_NEWS,
                title: t("Market Signals"),
                icon: "NotificationsActive",
                permissions: [Permission.SALES_ACTIVITY]
              }
            ]
          : []),
        {
          path: PATH.SALES_ACTIVITIES,
          title: t("Activities"),
          icon: "Assignment",
          permissions: [Permission.SALES_ACTIVITY]
        },
        {
          path: PATH.EMAIL_INTELLIGENCE,
          title: t("Email intelligence"),
          icon: "Email",
          permissions: [
            Permission.SALES_ACTIVITY,
            Permission.SUPPORT_CASE_VIEW,
            Permission.SUPPORT_CASE_MANAGE,
            Permission.TENANT
          ],
          breadcrumb: {
            title: t("Email intelligence")
          },
          subMenu: [
            {
              path: PATH.EMAIL_INTELLIGENCE_INBOX,
              title: t("Inbox"),
              icon: "Inbox",
              permissions: [
                Permission.SALES_ACTIVITY,
                Permission.SUPPORT_CASE_VIEW,
                Permission.SUPPORT_CASE_MANAGE,
                Permission.TENANT
              ],
              breadcrumb: { title: t("Email inbox") }
            },
            {
              path: PATH.EMAIL_INTELLIGENCE_REVIEW,
              title: t("Review outputs"),
              icon: "FactCheck",
              permissions: [
                Permission.SALES_ACTIVITY,
                Permission.SUPPORT_CASE_VIEW,
                Permission.SUPPORT_CASE_MANAGE,
                Permission.TENANT
              ],
              breadcrumb: { title: t("Review email outputs") }
            },
            {
              path: PATH.EMAIL_INTELLIGENCE_ANALYTICS,
              title: t("Response analytics"),
              icon: "Insights",
              permissions: [Permission.SALES_ACTIVITY, Permission.TENANT],
              breadcrumb: { title: t("Email response analytics") }
            },
            {
              path: PATH.EMAIL_INTELLIGENCE_RELATIONSHIPS,
              title: t("Relationships"),
              icon: "AccountTree",
              permissions: [
                Permission.SALES_ACTIVITY,
                Permission.SUPPORT_CASE_VIEW,
                Permission.SUPPORT_CASE_MANAGE,
                Permission.TENANT
              ],
              breadcrumb: { title: t("Email relationships") }
            },
            {
              path: PATH.EMAIL_INTELLIGENCE_CONTACTS,
              title: t("Contacts"),
              icon: "Contacts",
              permissions: [
                Permission.SALES_ACTIVITY,
                Permission.SUPPORT_CASE_VIEW,
                Permission.SUPPORT_CASE_MANAGE,
                Permission.TENANT
              ],
              breadcrumb: { title: t("Email contacts") }
            },
            {
              path: PATH.EMAIL_INTELLIGENCE_AUTOMATION_CANDIDATES,
              title: t("Automation candidates"),
              icon: "AutoFixHigh",
              permissions: [Permission.SALES_ACTIVITY, Permission.SALES_AUTOMATION, Permission.TENANT],
              breadcrumb: { title: t("Email automation candidates") }
            },
            {
              path: PATH.EMAIL_INTELLIGENCE_REPLY_RESEARCH,
              title: t("Reply research"),
              icon: "QuestionAnswer",
              permissions: [
                Permission.SALES_ACTIVITY,
                Permission.SUPPORT_CASE_VIEW,
                Permission.SUPPORT_CASE_MANAGE,
                Permission.TENANT
              ],
              breadcrumb: { title: t("Email reply research") }
            },
            {
              path: PATH.EMAIL_INTELLIGENCE_TEST_LAB,
              title: t("Test Lab proposals"),
              icon: "Router",
              permissions: [Permission.SUPPORT_CASE_VIEW, Permission.SUPPORT_CASE_MANAGE, Permission.TENANT],
              breadcrumb: { title: t("Email Test Lab proposals") }
            },
            {
              path: PATH.EMAIL_INTELLIGENCE_SUPPORT,
              title: t("Support queue"),
              icon: "SupportAgent",
              permissions: [Permission.SUPPORT_CASE_VIEW, Permission.SUPPORT_CASE_MANAGE, Permission.TENANT],
              breadcrumb: { title: t("Email support queue") }
            },
            {
              path: PATH.EMAIL_INTELLIGENCE_VENDOR_SUPPORT,
              title: t("Vendor support"),
              icon: "SupportAgent",
              permissions: [Permission.SUPPORT_CASE_VIEW, Permission.SUPPORT_CASE_MANAGE, Permission.TENANT],
              breadcrumb: { title: t("Vendor support email") }
            }
          ]
        },
        {
          path: PATH.SALES_CHANGE_REVIEW,
          title: t("Change Review"),
          icon: "SupportAgent",
          permissions: [Permission.CRM_CHANGE_HISTORY]
        },
        {
          path: PATH.SALES_PIPELINE,
          title: t("Sales Pipeline"),
          icon: "Inventory2",
          permissions: [Permission.OPPORTUNITY]
        },
        {
          path: PATH.SALES_SUPPORT_PIPELINE,
          title: t("Support Pipeline"),
          icon: "SupportAgent",
          permissions: [Permission.SUPPORT_CASE_VIEW, Permission.SUPPORT_CASE_MANAGE],
          breadcrumb: {
            title: t("Support Pipeline")
          }
        },
        {
          path: PATH.SALES_LEADS,
          title: "Lead Triage",
          icon: "ContactMail",
          permissions: [Permission.SALES_LEAD],
          breadcrumb: {
            title: "Lead Triage"
          }
        },
        {
          path: PATH.SALES_QUOTA,
          title: t("Quota"),
          icon: "EmojiEvents",
          permissions: [Permission.QUOTA]
        },
        ...(featureFlags.showSalesAutomation
          ? [
              {
                path: PATH.SALES_AUTOMATION,
                title: t("Automation"),
                icon: "ListAlt",
                permissions: [Permission.SALES_AUTOMATION]
              }
            ]
          : [])
      ]
    },
    {
      path: PATH.AGENTS,
      title: t("Agents"),
      icon: "SmartToy",
      permissions: [Permission.TRIAGE_SUB_AGENT, Permission.AGENT_PLATFORM_ADMIN],
      breadcrumb: {
        title: t("Agents")
      },
      subMenu: [
        {
          path: PATH.AGENTS,
          title: t("Agent Catalog"),
          icon: "ListAlt",
          permissions: [Permission.TRIAGE_SUB_AGENT, Permission.AGENT_PLATFORM_ADMIN],
          breadcrumb: {
            title: t("Agent Catalog")
          }
        },
        {
          path: PATH.AGENTS_SETTINGS,
          title: t("Agent Setup"),
          icon: "Settings",
          permissions: [Permission.AGENT_PLATFORM_ADMIN],
          roles: ["incident_responder", "runtime_operator", "security_auditor"],
          breadcrumb: {
            title: t("Agent Setup")
          }
        },
        {
          path: PATH.AGENTS_WORKSPACES,
          title: t("Workspaces"),
          icon: "Workspaces",
          permissions: [Permission.TRIAGE_SUB_AGENT],
          breadcrumb: {
            title: t("Workspaces")
          }
        },
        ...(featureFlags["standalone_agents.runtime_topology.enabled"] !== false
          ? [
              {
                path: PATH.AGENTS_RUNTIME,
                title: t("Execution"),
                icon: "AccountTree",
                permissions: [
                  Permission.TASKS,
                  Permission.TRIAGE_SUB_AGENT,
                  Permission.AGENT_PLATFORM_ADMIN,
                  Permission.TENANT,
                  Permission.AGENT_PLATFORM_RUNTIME
                ],
                roles: [...AGENT_RUNTIME_ADMIN_ROLES],
                breadcrumb: {
                  title: t("Agent Execution")
                }
              },
              ...(featureFlags["network_os.validation.enabled"]
                ? [
                    {
                      path: PATH.AGENTS_NETWORK_OS_VALIDATION,
                      title: t("Network OS validation"),
                      icon: "Router",
                      permissions: [Permission.AGENT_PLATFORM_RUNTIME],
                      roles: [...AGENT_RUNTIME_ADMIN_ROLES],
                      breadcrumb: {
                        title: t("Network OS validation")
                      }
                    },
                    {
                      path: PATH.AGENTS_NETWORK_AUTOMATION,
                      title: t("Network workspace"),
                      icon: "Router",
                      permissions: [Permission.AGENT_PLATFORM_RUNTIME],
                      roles: [...AGENT_RUNTIME_ADMIN_ROLES],
                      breadcrumb: {
                        title: t("Network workspace")
                      }
                    }
                  ]
                : [])
            ]
          : []),
        {
          path: PATH.AGENTS_LIST,
          title: t("AI Assistants"),
          icon: "SmartToy",
          permissions: [Permission.TRIAGE_SUB_AGENT],
          breadcrumb: {
            title: t("AI Assistants"),
            includeParams: true
          }
        },
        {
          path: PATH.AGENTS_TRIAGE_INBOX,
          title: t("Specialist Inbox"),
          icon: "Inbox",
          permissions: [Permission.TRIAGE_SUB_AGENT],
          breadcrumb: {
            title: t("Specialist Inbox"),
            includeParams: true
          }
        },
        {
          path: PATH.AI_TASK,
          title: t("Agent Tasks"),
          icon: "Assignment",
          permissions: [Permission.TASKS],
          breadcrumb: {
            title: t("Agent Tasks")
          }
        },
        {
          path: PATH.AGENTS_TRIAGE_ID,
          title: t("AI Assistant Detail"),
          icon: "SmartToy",
          permissions: [Permission.TRIAGE_SUB_AGENT],
          // Hide from sidebar but keep for breadcrumbs
          hidden: true,
          breadcrumb: {
            title: (params: Record<string, string>) => {
              const id = params?.id || "";
              if (!id) return t("AI Assistant");
              const cached = getAgentDisplayName(id);
              return cached || t("AI Assistant");
            },
            includeParams: true
          },
          subMenu: [
            {
              path: PATH.AGENTS_TRIAGE_ID_SUBAGENT_ID,
              title: t("Specialist"),
              icon: "SmartToy",
              permissions: [Permission.TRIAGE_SUB_AGENT],
              breadcrumb: {
                title: (params: Record<string, string>) => {
                  const id = params?.subId || "";
                  if (!id) return t("Specialist");
                  const cached = getAgentDisplayName(id);
                  return cached || t("Specialist");
                },
                includeParams: false
              }
              // TODO: Currently breadcrumbs are rendered before this data is cached.
              // This means on first page load, the breadcrumb title may not be available until
              // this useEffect runs and populates the cache. Consider one of these solutions:
              // 1. Use getServerSideProps to fetch and cache data server-side before render
              // 2. Use parent page to pre-populate cache before navigating here
              // 3. Make breadcrumb component listen to cache updates and re-render
            }
          ]
        },
        {
          path: PATH.AGENTS_TOOL,
          title: t("Tools"),
          icon: "Build",
          hidden: true,
          permissions: [Permission.AGENT_TOOLS],
          breadcrumb: {
            title: t("Tools")
          }
        },
        {
          path: PATH.AGENTS_INTENT_LIBRARY,
          title: t("Intent Library"),
          icon: "AutoFixHigh",
          permissions: [Permission.TRIAGE_SUB_AGENT],
          breadcrumb: {
            title: t("Intent Library")
          }
        }
      ]
    },
    {
      path: PATH.MARKETING,
      title: t("Data"),
      icon: "BusinessCenter",
      permissions: [...PermissionGroups.CRM_DATA],
      breadcrumb: {
        title: t("Data")
      },
      subMenu: [
        // Leads already lives under Sales -> Lead Triage.
        {
          path: PATH.CUSTOMER,
          title: t("Contacts"),
          icon: "Person",
          permissions: [Permission.SALES_CUSTOMER],
          breadcrumb: {
            title: t("Contacts"),
            includeParams: true
          }
        },
        {
          path: PATH.MARKETING_COMPANY,
          title: "Accounts",
          icon: "Apartment",
          permissions: [Permission.SALES_ACCOUNT],
          breadcrumb: {
            title: "Accounts",
            includeParams: true
          }
        },
        {
          path: PATH.CAMPAIGN,
          title: t("Campaigns"),
          icon: "Campaign",
          permissions: [Permission.SALES_CAMPAIGN],
          breadcrumb: {
            title: t("Campaigns"),
            includeParams: true
          }
        },
        {
          path: PATH.PRODUCT,
          title: t("Products"),
          icon: "Inventory2",
          permissions: [Permission.SALES_PRODUCT],
          breadcrumb: {
            title: t("Products"),
            includeParams: true
          }
        },
        {
          path: PATH.MARKETING_ASSETS,
          title: t("Assets"),
          icon: "Router",
          permissions: [EQUIPMENT_PERMISSIONS.view, EQUIPMENT_PERMISSIONS.manage],
          breadcrumb: {
            title: t("Assets")
          }
        },
        {
          path: PATH.MARKETING_FILES,
          title: t("Files"),
          icon: "Folder",
          permissions: [...PermissionGroups.CRM_DATA],
          breadcrumb: {
            title: t("Files")
          }
        },
        {
          path: PATH.KNOWLEDGE_MANAGER,
          title: t("Knowledge"),
          icon: "AutoAwesome",
          breadcrumb: {
            title: t("Knowledge")
          }
        },
        {
          path: PATH.OFFICE_GLOSSARY,
          title: t("Office glossary"),
          icon: "MenuBook",
          breadcrumb: {
            title: t("Office glossary")
          }
        }
      ]
    },
    {
      title: t("Apps"),
      icon: "Apps",
      subMenu: [
        createEngineeringAppMenu(t),
        itHelpdeskMenu,
        {
          path: PATH.TRAINING,
          title: t("Training"),
          icon: "School",
          breadcrumb: { title: t("Training") }
        },
        {
          path: PATH.BOM_BUILDER,
          title: t("BOM Builder"),
          icon: "Router",
          permissions: [EQUIPMENT_PERMISSIONS.view, EQUIPMENT_PERMISSIONS.manage],
          breadcrumb: {
            title: t("BOM Builder")
          }
        },
        {
          path: PATH.SINEBUZZ,
          title: t("Sinebuzz"),
          icon: "SmartToy",
          breadcrumb: { title: t("Sinebuzz") }
        },
        {
          path: PATH.SHOW_ADMIN,
          title: t("Show"),
          icon: "AutoFixHigh",
          productRouteKey: "show/admin"
        }
      ]
    },
    ...(featureFlags.showCoaching
      ? [
          {
            path: PATH.COACHING,
            title: t("Coaching"),
            icon: "EmojiEvents",
            subMenu: [
              {
                path: PATH.COACHING_TEAM_PERFORMING,
                title: t("Team Performing"),
                icon: "Groups"
              }
            ]
          }
        ]
      : []),

    {
      path: PATH.SETTING,
      title: t("Settings"),
      icon: "Settings",
      breadcrumb: {
        title: t("Settings")
      },
      subMenu: getSettingsMenuItems(t, isSandboxTenant)
    }
    // {
    //   path: PATH.TODAY,
    //   title: 'today',
    //   icon: 'Today'
    // },

    // {
    //   path: PATH.AI_ASSISTANT,
    //   title: "ai-assistant",
    //   icon: "Android"
    // },
  ];
}
